import {
  EventStore,
  ResourceStore,
  ResourceTimeRangeStore,
  SchedulerEventModel,
  SchedulerProConfig,
  TimeRangeStore,
} from '@bryntum/schedulerpro';
import { Composer } from 'vue-i18n';

import { baseSchedulerProConfig } from '@/common/bryntum/baseSchedulerProConfig';
import { useOrderScheduleDefaultInRowSorting } from '@/components/schedule/inRowSorting';
import { SchedulerViewPreset, useSchedulerViewPresets } from '@/features/schedule/bryntum/presets';
import { EVENT_LAYOUT_STACK } from '@/features/schedule/bryntum/resources/const';
import { useScheduleViewStore } from '@/features/scheduleViews/scheduleViewStore';
import { NodeName } from '@/repositories/utils/cache';
import { LoggingService } from '@/services/logging/types';
import { setViewPreset } from '@/services/store/schedule/actions/initialize/base';
import { getColumns } from '@/services/store/schedule/config/columns';
import { getCustomIsValidDependency } from '@/services/store/schedule/config/listeners/dependencies';
import { getScheduleListeners } from '@/services/store/schedule/config/listeners/schedule';
import { getEventRenderer } from '@/services/store/schedule/config/renderers/eventRenderer';
import { ScheduleStore } from '@/services/store/schedule/store';
import {
  CustomDependencyModel,
  CustomDependencyStore,
  CustomEventModel,
  CustomResourceModel,
} from '@/services/store/schedule/utils/overrides';

import { getPlaceholderEventId } from '../parsers/base';
import { DryingBreakEventParser } from '../parsers/dryingBreak';
import { SchedulerEvent } from '../types/types';

const FIFTEEN_YEARS_IN_MILLISECONDS = 15 * 365 * 24 * 3600000;

const getEventId = (event: SchedulerEvent) => {
  return event.entity === NodeName.DRYING_BREAK
    ? DryingBreakEventParser.dryingBreakIdToOrderId(event.id)
    : event.id;
};

export function setConfig(
  store: ScheduleStore,
  i18n: Composer,
  loggingService: LoggingService,
): Partial<SchedulerProConfig> {
  const scheduleViewStore = useScheduleViewStore();
  const presets = useSchedulerViewPresets(i18n);
  scheduleViewStore.zoomPresets = presets.map((preset) => preset.name as SchedulerViewPreset);
  const initialPreset = setViewPreset(scheduleViewStore.zoomPreset);
  const dependencyStore = new CustomDependencyStore({ modelClass: CustomDependencyModel });
  dependencyStore.injectCustomIsValidDependencyFunction(getCustomIsValidDependency(true));
  const defaultInRowSorting = useOrderScheduleDefaultInRowSorting();

  return {
    ...baseSchedulerProConfig,
    columns: getColumns(store, i18n, loggingService),
    createEventOnDblClick: false,
    dependencyStore,
    deselectAllOnScheduleClick: false,
    deselectOnClick: false,
    destroyStores: true,
    displaySchedulingIssueResolutionPopup: false,
    enableDeleteKey: false,
    // during tests event and row animations contribute to flakiness
    enableEventAnimations: false,
    eventLayout: {
      type: EVENT_LAYOUT_STACK,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore Bryntum didn't add groupBy to the type definition
      groupBy: (event: SchedulerEvent) => {
        const eventId = getEventId(event);
        // Placeholder events are always at the top
        if (eventId === getPlaceholderEventId()) {
          return -1;
        }

        if (event.entity === NodeName.SECTION || event.entity === NodeName.SECTION_BASEPLAN) {
          return -1;
        }

        const lane = store.sorting.elementLaneMap.get(eventId);
        return lane ?? 0;
      },
    },
    eventRenderer: getEventRenderer(store),
    eventStore: new EventStore({
      modelClass: CustomEventModel,
    }),
    fullRowRefresh: false,
    infiniteScroll: true,
    isEventSelectable() {
      return !store.utils.disableEventInteractions;
    },
    // @ts-expect-error NOTE: The typing in the listeners is based on the static bryntum types models, however, we use custom models, so there always will be a type mismatch
    listeners: getScheduleListeners(store, i18n, loggingService),
    multiEventSelect: true,
    overlappingEventSorter: (a: SchedulerEventModel, b: SchedulerEventModel) => {
      let result = 0;
      const aId = getEventId(a as SchedulerEvent);
      const bId = getEventId(b as SchedulerEvent);

      // Handle placeholder events separately
      if (aId === getPlaceholderEventId() || bId === getPlaceholderEventId()) {
        const sameStart = new Date(a.startDate).getTime() === new Date(b.startDate).getTime();
        if (!sameStart) {
          return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
        }
        return new Date(b.endDate).getTime() - new Date(a.endDate).getTime();
      }

      try {
        result = defaultInRowSorting(aId, bId);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        // In some cases bryntum calls this while events have not been synced to the stores yet, which we ignore as it will eventually succeed
      }
      return result;
    },
    presets,
    project: {
      enableProgressNotifications: false,
      // fixes bug that dates where changed to 23.00 when overlapping DST time
      adjustDurationToDST: true,
      // avoids implicit constraint setting when updating events
      addConstraintOnDateSet: false,
      maxCalendarRange: FIFTEEN_YEARS_IN_MILLISECONDS,
    },
    resourceStore: new ResourceStore({
      modelClass: CustomResourceModel,
      tree: true,
      transformFlatData: true,
    }),
    resourceTimeRangeStore: new ResourceTimeRangeStore(),
    selectionMode: {
      row: true,
      cell: false,
    },
    snapRelativeToEventStartDate: true,
    timeRangeStore: new TimeRangeStore(),
    useInitialAnimation: false,
    viewPreset: initialPreset,
    visibleDate: {
      date: new Date(scheduleViewStore.scrollDate ?? Date.now()),
      block: 'center',
    },
    zoomKeepsOriginalTimespan: true,
    zoomOnMouseWheel: false,
    transitionDuration: 50,
  };
}
